/**
 * ProcessingStateModal Component
 * A comprehensive modal showing full state details for a tender
 */

import Modal from './Modal';
import ProcessingStateTimeline from './ProcessingStateTimeline';
import ProcessingStateFlow from './ProcessingStateFlow';
import {
    getStateInfo,
    getAvailableActions,
    isProcessingState,
    ACTIONS,
} from '../utils/processingState';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

const ProcessingStateModal = ({
    isOpen,
    onClose,
    tender,
    onAction,
}) => {
    if (!tender) return null;

    const currentState = tender.processingState || 'DRAFT';
    const stateInfo = getStateInfo(currentState);
    const availableActions = getAvailableActions(currentState);
    const isInProgress = isProcessingState(currentState);
    const StateIcon = stateInfo.Icon;

    const handleAction = (actionKey) => {
        if (onAction) {
            onAction(actionKey, tender);
        }
    };

    // Get color classes based on state
    const getHeaderColorClasses = () => {
        const colorMap = {
            gray: 'bg-gray-800 border-gray-700',
            blue: 'bg-blue-50 border-blue-200',
            yellow: 'bg-yellow-50 border-yellow-200',
            green: 'bg-green-50 border-green-200',
            emerald: 'bg-emerald-50 border-emerald-200',
            red: 'bg-red-50 border-red-200',
        };
        return colorMap[stateInfo.color] || colorMap.gray;
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Pipeline Status"
            size="xl"
        >
            <div className="space-y-6">
                {/* Current State Header */}
                <div className={` p-4 border ${getHeaderColorClasses()}`}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className={`p-3 ${stateInfo.bgClass}`}>
                                <StateIcon className={`h-8 w-8 ${stateInfo.textClass} ${isInProgress ? 'animate-spin' : ''}`} />
                            </div>
                            <div>
                                <h3 className={`text-xl font-bold ${stateInfo.textClass}`}>
                                    {stateInfo.label}
                                </h3>
                                <p className="text-sm text-gray-400">
                                    {stateInfo.description}
                                </p>
                                {tender.stateChangedAt && (
                                    <p className="text-xs text-gray-500 mt-1">
                                        Since {new Date(tender.stateChangedAt).toLocaleString()}
                                    </p>
                                )}
                            </div>
                        </div>
                        {isInProgress && (
                            <div className="flex items-center gap-2 text-yellow-700">
                                <ArrowPathIcon className="h-5 w-5 animate-spin" />
                                <span className="text-sm">Processing...</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Tender Info */}
                <div className="bg-gray-800 p-4">
                    <h4 className="text-sm font-medium text-gray-300 mb-3">Tender Information</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <span className="text-gray-500">Title:</span>
                            <div className="text-white font-medium truncate" title={tender.title}>
                                {tender.title}
                            </div>
                        </div>
                        <div>
                            <span className="text-gray-500">External ID:</span>
                            <div className="text-white font-mono text-xs">
                                {tender.externalId}
                            </div>
                        </div>
                        {tender.deadline && (
                            <div>
                                <span className="text-gray-500">Deadline:</span>
                                <div className="text-white">
                                    {new Date(tender.deadline).toLocaleDateString()}
                                </div>
                            </div>
                        )}
                        {tender.documents && (
                            <div>
                                <span className="text-gray-500">Documents:</span>
                                <div className="text-white">
                                    {tender.documents.length} file(s)
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Timeline Progress */}
                <div className="bg-gray-800 p-4">
                    <ProcessingStateTimeline
                        currentState={currentState}
                        stateChangedAt={tender.stateChangedAt}
                        showEnrichmentBranch={['ENRICHING', 'ENRICHED'].includes(currentState)}
                    />
                </div>

                {/* State Flow Diagram */}
                <div className="bg-gray-800 p-4">
                    <h4 className="text-sm font-medium text-gray-300 mb-3">State Machine</h4>
                    <ProcessingStateFlow
                        currentState={currentState}
                        showLegend={true}
                    />
                </div>

                {/* Available Actions */}
                {availableActions.length > 0 && (
                    <div className="bg-gray-800 p-4">
                        <h4 className="text-sm font-medium text-gray-300 mb-3">Available Actions</h4>
                        <div className="flex flex-wrap gap-3">
                            {availableActions.map(actionKey => {
                                const action = ACTIONS[actionKey];
                                if (!action) return null;

                                const ActionIcon = action.Icon;
                                const colorMap = {
                                    blue: 'bg-blue-600 hover:bg-blue-500 text-white',
                                    cyan: 'bg-cyan-600 hover:bg-cyan-500 text-white',
                                    purple: 'bg-purple-600 hover:bg-purple-500 text-white',
                                    green: 'bg-green-600 hover:bg-green-500 text-white',
                                };
                                const buttonClass = colorMap[action.color] || 'bg-gray-600 hover:bg-gray-500 text-white';

                                return (
                                    <button
                                        key={actionKey}
                                        onClick={() => handleAction(actionKey)}
                                        className={`
 flex items-center gap-2 px-4 py-2 
 font-medium transition-all hover:scale-105
 ${buttonClass}
 `}
                                    >
                                        <ActionIcon className="h-5 w-5" />
                                        <span>{action.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                        <p className="mt-3 text-xs text-gray-500">
                            Click an action to start the corresponding job for this tender.
                        </p>
                    </div>
                )}

                {/* No actions available message */}
                {availableActions.length === 0 && (
                    <div className="bg-gray-800 p-4 text-center">
                        <p className="text-gray-400">
                            {isInProgress
                                ? 'Please wait while the current operation completes...'
                                : currentState === 'ASSESSED'
                                    ? 'This tender has completed the full pipeline.'
                                    : currentState === 'ARCHIVED'
                                        ? 'This tender is archived and cannot be modified.'
                                        : 'No actions available for this state.'
                            }
                        </p>
                    </div>
                )}

                {/* Close button */}
                <div className="flex justify-end pt-4 border-t border-gray-700">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-600 text-white hover:bg-gray-500 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default ProcessingStateModal;
